import { useState, useMemo } from "react"
import { useForm } from "@inertiajs/react"
import { Users, BookOpen, GraduationCap, CheckCircle2, ArrowLeft } from "lucide-react"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { formatPhone, isValidEmail } from "@/lib/form-utils"
import { COUNTRIES } from "@/lib/countries"

const BECOME_COACH_URL = "https://events.kingdomfactor.us/becomeacoach-page"

export function GetStartedDialog({ open, onOpenChange, defaultSource = "header_modal" }) {
  const [step, setStep] = useState("chooser") // chooser | form | success
  const [clientErrors, setClientErrors] = useState({})
  const [submittedCountry, setSubmittedCountry] = useState("US")

  const form = useForm({
    name: "",
    email: "",
    phone: "",
    company: "",
    job_title: "",
    zip_code: "",
    country: "US",
    source: defaultSource,
    referred_by_coach_id: "",
    referral_coach_name: "",
    website_url: "", // honeypot
  })

  const validate = () => {
    const errors = {}
    const { name, email, zip_code, country } = form.data

    if (!name.trim()) errors.name = "Name is required"
    if (!email.trim()) {
      errors.email = "Email is required"
    } else if (!isValidEmail(email)) {
      errors.email = "Please enter a valid email address"
    }
    if (country === "US") {
      if (!zip_code.trim()) {
        errors.zip_code = "Zip code is required"
      } else if (!/^\d{5}$/.test(zip_code.trim())) {
        errors.zip_code = "Please enter a 5-digit US zip code"
      }
    }

    setClientErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!validate()) return

    setSubmittedCountry(form.data.country)
    form.post("/prospect-leads", {
      preserveScroll: true,
      onSuccess: () => setStep("success"),
    })
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      setStep("chooser")
      setClientErrors({})
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value)
    form.setData("phone", formatted)
  }

  const setField = (field, value) => {
    form.setData(field, value)
    if (clientErrors[field]) {
      setClientErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const errorFor = (field) => clientErrors[field] || form.errors[field]

  // If defaultSource is member_briefing, skip chooser and go straight to form
  const effectiveStep = defaultSource === "member_briefing" && step === "chooser" ? "form" : step

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        {effectiveStep === "success" && <SuccessStep isInternational={submittedCountry !== "US"} />}
        {effectiveStep === "chooser" && (
          <ChooserStep
            onJoinPeerGroup={() => setStep("form")}
            onClose={() => handleClose(false)}
          />
        )}
        {effectiveStep === "form" && (
          <FormStep
            form={form}
            clientErrors={clientErrors}
            errorFor={errorFor}
            setField={setField}
            handlePhoneChange={handlePhoneChange}
            handleSubmit={handleSubmit}
            onBack={defaultSource === "member_briefing" ? null : () => setStep("chooser")}
          />
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

function ChooserStep({ onJoinPeerGroup, onClose }) {
  const cards = [
    {
      icon: Users,
      title: "Join a Peer Group",
      description: "Connect with a local coach and explore membership in a Kingdom Factor peer group.",
      action: onJoinPeerGroup,
    },
    {
      icon: BookOpen,
      title: "Share Your Story",
      description: "Be featured in the KF Collective and share how faith shapes your leadership.",
      action: () => { window.location.href = "/share-your-story" },
    },
    {
      icon: GraduationCap,
      title: "Become a Coach",
      description: "Learn about becoming a certified Kingdom Factor coach.",
      action: () => { window.open(BECOME_COACH_URL, "_blank", "noopener,noreferrer") },
    },
  ]

  return (
    <>
      <ResponsiveDialogHeader>
        <ResponsiveDialogTitle className="font-display text-xl">
          Get Started with Kingdom Factor
        </ResponsiveDialogTitle>
        <ResponsiveDialogDescription>
          Choose the path that fits where you are on your journey.
        </ResponsiveDialogDescription>
      </ResponsiveDialogHeader>

      <div className="grid gap-3 py-4 px-4 sm:px-0">
        {cards.map((card) => (
          <button
            key={card.title}
            onClick={card.action}
            className="flex items-start gap-4 p-4 rounded-lg border border-zinc-200 text-left hover:border-kf-red hover:bg-red-50/50 transition-colors group"
          >
            <div className="w-10 h-10 rounded-full bg-zinc-100 group-hover:bg-kf-red/10 flex items-center justify-center flex-shrink-0">
              <card.icon className="w-5 h-5 text-zinc-600 group-hover:text-kf-red" />
            </div>
            <div>
              <p className="font-semibold text-zinc-900 group-hover:text-kf-red">{card.title}</p>
              <p className="text-sm text-zinc-500 mt-0.5">{card.description}</p>
            </div>
          </button>
        ))}
      </div>

      <ResponsiveDialogFooter>
        <ResponsiveDialogClose asChild>
          <Button type="button" variant="outline">
            Cancel
          </Button>
        </ResponsiveDialogClose>
      </ResponsiveDialogFooter>
    </>
  )
}

function FormStep({ form, clientErrors, errorFor, setField, handlePhoneChange, handleSubmit, onBack }) {
  return (
    <form onSubmit={handleSubmit}>
      <ResponsiveDialogHeader>
        <ResponsiveDialogTitle className="font-display text-xl">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center text-sm text-zinc-500 hover:text-zinc-900 mb-1 font-normal"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </button>
          )}
          <span className="block">Join a Peer Group</span>
        </ResponsiveDialogTitle>
        <ResponsiveDialogDescription>
          Fill out the form below and we'll connect you with a Kingdom Factor coach.
        </ResponsiveDialogDescription>
      </ResponsiveDialogHeader>

      {/* Honeypot — hidden from users */}
      <div className="hidden" aria-hidden="true">
        <Input
          type="text"
          name="website_url"
          tabIndex={-1}
          autoComplete="off"
          value={form.data.website_url}
          onChange={(e) => form.setData("website_url", e.target.value)}
        />
      </div>

      <div className="space-y-4 py-4 px-4 sm:px-0">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gs-name">Name *</Label>
            <Input
              id="gs-name"
              value={form.data.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="Your full name"
            />
            {errorFor("name") && (
              <p className="text-sm text-red-600">{errorFor("name")}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="gs-email">Email *</Label>
            <Input
              id="gs-email"
              type="email"
              value={form.data.email}
              onChange={(e) => setField("email", e.target.value)}
              placeholder="you@example.com"
            />
            {errorFor("email") && (
              <p className="text-sm text-red-600">{errorFor("email")}</p>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gs-phone">Phone</Label>
            <Input
              id="gs-phone"
              type="tel"
              value={form.data.phone}
              onChange={handlePhoneChange}
              placeholder="(555) 123-4567"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="gs-country">Country *</Label>
            <select
              id="gs-country"
              value={form.data.country}
              onChange={(e) => {
                const newCountry = e.target.value
                if (newCountry !== "US") {
                  form.setData((prev) => ({ ...prev, country: newCountry, zip_code: "" }))
                } else {
                  form.setData("country", newCountry)
                }
                if (clientErrors.country) {
                  setClientErrors((prev) => ({ ...prev, country: undefined }))
                }
                if (clientErrors.zip_code) {
                  setClientErrors((prev) => ({ ...prev, zip_code: undefined }))
                }
              }}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gs-zip">
              {form.data.country === "US" ? "Zip Code *" : "Postal Code"}
            </Label>
            <Input
              id="gs-zip"
              value={form.data.zip_code}
              onChange={(e) => setField("zip_code", e.target.value)}
              placeholder={form.data.country === "US" ? "12345" : "Postal code (optional)"}
              {...(form.data.country === "US" ? { maxLength: 5 } : {})}
            />
            {errorFor("zip_code") && (
              <p className="text-sm text-red-600">{errorFor("zip_code")}</p>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="gs-company">Company</Label>
            <Input
              id="gs-company"
              value={form.data.company}
              onChange={(e) => setField("company", e.target.value)}
              placeholder="Your company"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="gs-title">Job Title</Label>
            <Input
              id="gs-title"
              value={form.data.job_title}
              onChange={(e) => setField("job_title", e.target.value)}
              placeholder="Your role"
            />
          </div>
        </div>

        <CoachReferralField
          coachId={form.data.referred_by_coach_id}
          coachName={form.data.referral_coach_name}
          onSelectCoach={(id) => form.setData("referred_by_coach_id", id ?? "")}
          onTypeName={(name) => form.setData("referral_coach_name", name)}
        />

        <p className="text-xs text-zinc-400">
          By submitting, you agree to be contacted by a Kingdom Factor coach.
        </p>
      </div>

      <ResponsiveDialogFooter>
        <ResponsiveDialogClose asChild>
          <Button type="button" variant="outline">
            Cancel
          </Button>
        </ResponsiveDialogClose>
        <Button
          type="submit"
          variant="gradient"
          disabled={form.processing}
        >
          {form.processing ? "Submitting..." : "Submit"}
        </Button>
      </ResponsiveDialogFooter>
    </form>
  )
}

// "Were you referred by a KF coach?" — a searchable combobox over active
// coaches with a free-text fallback. The coach list is fetched lazily the
// first time the prospect answers "Yes" (kept out of every public page load).
// Selecting a coach routes the lead straight to them; a typed name that matches
// no coach is sent as free text for an admin to follow up.
function CoachReferralField({ coachId, coachName, onSelectCoach, onTypeName }) {
  const [referred, setReferred] = useState(coachId || coachName ? true : null)
  const [coaches, setCoaches] = useState([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [query, setQuery] = useState(coachName || "")
  const [focused, setFocused] = useState(false)

  const loadCoaches = async () => {
    if (loaded || loading) return
    setLoading(true)
    try {
      const res = await fetch("/prospect-leads/coaches", {
        headers: { Accept: "application/json" },
      })
      if (res.ok) setCoaches(await res.json())
    } catch {
      // Network error — the prospect can still type a name (free-text fallback).
    } finally {
      setLoading(false)
      setLoaded(true)
    }
  }

  const choose = (yes) => {
    setReferred(yes)
    if (yes) {
      loadCoaches()
    } else {
      onSelectCoach(null)
      onTypeName("")
      setQuery("")
    }
  }

  const selectedCoach = useMemo(
    () => (coachId ? coaches.find((c) => c.id?.toString() === coachId?.toString()) : null),
    [coaches, coachId]
  )

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    return coaches.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 6)
  }, [coaches, query])

  const handleInput = (val) => {
    setQuery(val)
    onSelectCoach(null) // editing means they're searching again
    onTypeName(val) // capture as free text until/unless they pick a coach
  }

  const pick = (coach) => {
    onSelectCoach(coach.id)
    onTypeName("")
    setQuery(coach.name)
    setFocused(false)
  }

  const showList = referred === true && focused && query.trim().length > 0 && !selectedCoach

  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 p-4">
      <p className="text-sm font-medium text-zinc-900">
        Were you referred by a Kingdom Factor coach?
      </p>
      <div className="flex gap-2">
        <Button
          type="button"
          variant={referred === true ? "gradient" : "outline"}
          size="sm"
          onClick={() => choose(true)}
        >
          Yes
        </Button>
        <Button
          type="button"
          variant={referred === false ? "secondary" : "outline"}
          size="sm"
          onClick={() => choose(false)}
        >
          No
        </Button>
      </div>

      {referred === true && (
        <div className="space-y-1.5">
          <Label htmlFor="gs-coach">Coach's name</Label>
          <div className="relative">
            <Input
              id="gs-coach"
              value={query}
              autoComplete="off"
              placeholder={loading ? "Loading coaches…" : "Start typing your coach's name"}
              onChange={(e) => handleInput(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setTimeout(() => setFocused(false), 150)}
            />
            {showList && (
              <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-zinc-200 bg-white shadow-lg">
                {matches.length > 0 ? (
                  matches.map((coach) => (
                    <button
                      key={coach.id}
                      type="button"
                      className="block w-full px-3 py-2 text-left text-sm text-zinc-900 hover:bg-zinc-100"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        pick(coach)
                      }}
                    >
                      {coach.name}
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-zinc-500">
                    No coach matches “{query.trim()}”. We'll pass this name along to our team.
                  </div>
                )}
              </div>
            )}
          </div>
          {selectedCoach ? (
            <p className="text-xs text-green-700">
              Selected: <span className="font-medium">{selectedCoach.name}</span>
            </p>
          ) : query.trim() ? (
            <p className="text-xs text-zinc-500">
              We'll connect you with “{query.trim()}.” Pick from the list if you see them.
            </p>
          ) : null}
        </div>
      )}
    </div>
  )
}

function SuccessStep({ isInternational }) {
  return (
    <div className="text-center py-4">
      <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
        <CheckCircle2 className="w-7 h-7 text-green-600" />
      </div>
      <ResponsiveDialogHeader className="text-center">
        <ResponsiveDialogTitle className="text-xl">
          You're All Set!
        </ResponsiveDialogTitle>
        <ResponsiveDialogDescription className="text-base text-zinc-600 mt-2">
          Thank you for your interest in Kingdom Factor! A Kingdom Factor coach will reach out to you soon{isInternational ? "" : " in your area"}.
        </ResponsiveDialogDescription>
      </ResponsiveDialogHeader>
      <ResponsiveDialogFooter className="mt-6 sm:justify-center">
        <ResponsiveDialogClose asChild>
          <Button variant="gradient">Done</Button>
        </ResponsiveDialogClose>
      </ResponsiveDialogFooter>
    </div>
  )
}
